import React, { useCallback, useEffect, useState } from "react";
import { fetchAuditLog, getCurrency } from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

// Adjustments — the audit trail of sensitive money/sale actions (discounts,
// bill reopens, order cancellations): who did it, who approved, why. Read-only,
// for manager/owner loss-prevention review.
const S = {
  en: {
    title: "Adjustments", sub: "Every discount, reopened bill and cancelled order — who did it, who approved, and why.",
    all: "All", discount: "Discounts", reopen: "Reopens", cancel_order: "Cancellations",
    when: "When", what: "Action", ref: "Ref", amount: "Amount", reason: "Reason", by: "By", approved: "Approved by",
    none: "No adjustments recorded yet.", loading: "Loading…",
    a_discount: "Discount", a_reopen: "Bill reopened", a_cancel_order: "Order cancelled", no_reason: "—",
  },
  am: {
    title: "ማስተካከያዎች", sub: "እያንዳንዱ ቅናሽ፣ እንደገና የተከፈተ ደረሰኝና የተሰረዘ ትዕዛዝ — ማን እንዳደረገው፣ ማን እንዳጸደቀው፣ ለምን።",
    all: "ሁሉም", discount: "ቅናሾች", reopen: "እንደገና መከፈት", cancel_order: "ስረዛዎች",
    when: "መቼ", what: "ተግባር", ref: "ማጣቀሻ", amount: "መጠን", reason: "ምክንያት", by: "በ", approved: "ያጸደቀው",
    none: "እስካሁን ማስተካከያ የለም።", loading: "በመጫን ላይ…",
    a_discount: "ቅናሽ", a_reopen: "ደረሰኝ እንደገና ተከፈተ", a_cancel_order: "ትዕዛዝ ተሰረዘ", no_reason: "—",
  },
  om: {
    title: "Sirreeffamawwan", sub: "Hir'isni, nagaheen deebi'ee banames fi ajajni haqames hundi — eenyu akka raawwate, eenyu akka mirkaneesse, maaliif.",
    all: "Hunda", discount: "Hir'isawwan", reopen: "Deebi'anii banaman", cancel_order: "Haqamuu",
    when: "Yeroo", what: "Gochaa", ref: "Wabii", amount: "Baay'ina", reason: "Sababa", by: "Kan raawwate", approved: "Kan mirkaneesse",
    none: "Hanga ammaa sirreeffamni hin galmoofne.", loading: "Fe'aa jira…",
    a_discount: "Hir'isa", a_reopen: "Nagahee deebi'ee baname", a_cancel_order: "Ajajni haqame", no_reason: "—",
  },
};

const ACTION_COLOR = { discount: "#BA7517", reopen: "#7F77DD", cancel_order: "#A32D2D" };

export default function AdjustmentsPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";
  const money = (v) => (v == null ? "—" : formatPrice(v, cur));

  const [filter, setFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    fetchAuditLog(filter ? { action: filter } : {})
      .then((d) => { setRows(d || []); setErr(""); })
      .catch((e) => setErr(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not load."))
      .finally(() => setLoading(false));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const when = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };
  const tabBtn = (val, label) => (
    <button className={"btn small" + (filter === val ? "" : " secondary")} onClick={() => setFilter(val)}>{label}</button>
  );

  return (
    <>
      <h1>🧾 {t("title")}</h1>
      <p className="muted" style={{ marginTop: -4, maxWidth: 720 }}>{t("sub")}</p>

      <div style={{ display: "flex", gap: 6, margin: "12px 0", flexWrap: "wrap" }}>
        {tabBtn("", t("all"))}
        {tabBtn("discount", t("discount"))}
        {tabBtn("reopen", t("reopen"))}
        {tabBtn("cancel_order", t("cancel_order"))}
      </div>

      {err && <div className="error" style={{ marginTop: 8 }}>{err}</div>}
      {loading ? <p className="muted">{t("loading")}</p> : rows.length === 0 ? (
        <p className="muted">{t("none")}</p>
      ) : (
        <div className="card" style={{ marginTop: 4, overflowX: "auto" }}>
          <table className="data" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr style={{ background: "#008080" }}>
              {["when", "what", "ref", "amount", "reason", "by", "approved"].map((k) => (
                <th key={k} style={{ color: "#fff", textAlign: k === "amount" ? "right" : "left", padding: "8px 10px" }}>{t(k)}</th>
              ))}
            </tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} style={{ background: i % 2 ? "#f2f9f8" : "#fff" }}>
                  <td style={{ padding: "6px 10px", whiteSpace: "nowrap", fontSize: 12 }}>{when(r.created_at)}</td>
                  <td style={{ padding: "6px 10px" }}>
                    <span style={{ color: ACTION_COLOR[r.action] || "#333", fontWeight: 600, fontSize: 13 }}>{t("a_" + r.action) || r.action}</span>
                  </td>
                  <td style={{ padding: "6px 10px", fontSize: 12 }} className="muted">{r.entity_type} #{r.entity_id}</td>
                  <td style={{ padding: "6px 10px", textAlign: "right", whiteSpace: "nowrap" }}>{money(r.amount)}</td>
                  <td style={{ padding: "6px 10px", maxWidth: 260 }}>{r.reason || t("no_reason")}</td>
                  <td style={{ padding: "6px 10px", fontSize: 12 }}>{r.actor_name || "—"} {r.actor_role ? <span className="muted">({r.actor_role})</span> : null}</td>
                  <td style={{ padding: "6px 10px", fontSize: 12 }}>{r.approved_by_name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
